import { captureException } from '@sentry/react';
import {
    V2ServiceAreaContentRequest,
    V2ServiceAreaContentResponse,
    V2ServiceAreaContentType,
} from './v2NeighborhoodTypes';

type V2ErrorEnvelope = { error?: { code?: string; message?: string } };

type FetchV2ServiceAreaContentPageParams<T extends V2ServiceAreaContentType> = {
    url: string;
    body: V2ServiceAreaContentRequest<T>;
};

/**
 * Fetch one page of a service area's jobs or reviews from
 * `POST /widget/v2/serviceArea/content`.
 *
 * Generic over the content `type` so the response's element shape follows the
 * request's — asking for reviews and reading the result as jobs is a type error
 * rather than a silent mis-adapt. Returns undefined on any transport or service
 * failure (an HTTP error, or the `{ error }` envelope the service returns at
 * HTTP 200), reported to Sentry, so callers can fall back to v1.
 *
 * Shared by the map's per-area job walk (`fetchV2ServiceAreaByPostcode`) and the
 * featured/pop-out fetchers (`fetchV2FeaturedContent`).
 */
export const fetchV2ServiceAreaContentPage = async <
    T extends V2ServiceAreaContentType
>({
    url,
    body,
}: FetchV2ServiceAreaContentPageParams<T>): Promise<
    V2ServiceAreaContentResponse<T> | undefined
> => {
    try {
        const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });

        if (!res.ok) {
            captureException(
                new Error(
                    `V2 serviceArea/content fetch failed: HTTP ${res.status}`
                ),
                { extra: { ...body } }
            );
            return undefined;
        }

        const parsed = (await res.json()) as V2ServiceAreaContentResponse<T> &
            V2ErrorEnvelope;

        if (parsed.error) {
            captureException(
                new Error(
                    `V2 serviceArea/content fetch failed: ${parsed.error.code}: ${parsed.error.message}`
                ),
                { extra: { ...body } }
            );
            return undefined;
        }

        return parsed;
    } catch (e) {
        captureException(e, { extra: { ...body } });
        return undefined;
    }
};
